import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  Loader2,
  PanelLeftClose,
  PanelLeft,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

interface PDFViewerProps {
  url: string;
  className?: string;
  /** Show bottom control bar (default: true) */
  showBottomControls?: boolean;
  /** Show thumbnail sidebar (default: true) */
  showThumbnails?: boolean;
  /** Initial page to scroll to when PDF loads (default: 1) */
  initialPage?: number;
  /** Single page mode - display only one page at a time with prev/next navigation (default: false) */
  singlePageMode?: boolean;
}

// Zoom constants
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;
const DEFAULT_SCALE = 1.0; // 1.0 = fit to width

// Thumbnail constants
const DEFAULT_THUMBNAIL_WIDTH = 80;
const MIN_THUMBNAIL_SCALE = 0.5;
const MAX_THUMBNAIL_SCALE = 1.5;

/**
 * Individual page thumbnail component
 */
interface ThumbnailProps {
  pdfDoc: pdfjsLib.PDFDocumentProxy;
  pageNum: number;
  isSelected: boolean;
  onClick: () => void;
  thumbnailWidth: number;
}

function PageThumbnail({ pdfDoc, pageNum, isSelected, onClick, thumbnailWidth }: ThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const renderAttemptedRef = useRef(false);
  const lastWidthRef = useRef(thumbnailWidth);

  useEffect(() => {
    // Re-render if width changed significantly
    if (Math.abs(lastWidthRef.current - thumbnailWidth) > 5) {
      renderAttemptedRef.current = false;
      lastWidthRef.current = thumbnailWidth;
    }

    if (!canvasRef.current || renderAttemptedRef.current) {
      return;
    }
    renderAttemptedRef.current = true;

    const renderThumbnail = async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const canvas = canvasRef.current;
        if (!canvas) {
          return;
        }

        const context = canvas.getContext('2d');
        if (!context) {
          return;
        }

        // Calculate scale to fit thumbnail width
        const viewport = page.getViewport({ scale: 1 });
        const scale = thumbnailWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        await page.render({
          canvasContext: context,
          viewport: scaledViewport
        }).promise;

        setLoading(false);
      } catch {
        // Silently handle errors for thumbnails
        setLoading(false);
      }
    };

    renderThumbnail();
  }, [pdfDoc, pageNum, thumbnailWidth]);

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center gap-1 p-1 rounded-md transition-all',
        'hover:bg-muted/80',
        isSelected && 'bg-primary/10 ring-2 ring-primary'
      )}
    >
      <div className="relative bg-white shadow-sm rounded overflow-hidden">
        {loading && (
          <div
            className="flex items-center justify-center bg-muted/50"
            style={{ width: thumbnailWidth, height: thumbnailWidth * 1.4 }}
          >
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={cn(loading && 'hidden')}
          style={{ maxWidth: thumbnailWidth }}
        />
      </div>
      <span className={cn(
        'text-[9px] font-medium',
        isSelected ? 'text-primary' : 'text-muted-foreground'
      )}>
        {pageNum}
      </span>
    </button>
  );
}

/**
 * Individual page in continuous scroll view
 */
interface PageCanvasProps {
  pdfDoc: pdfjsLib.PDFDocumentProxy;
  pageNum: number;
  scale: number;
  containerWidth: number;
}

function PageCanvas({ pdfDoc, pageNum, scale, containerWidth }: PageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    // Increment render ID - any in-flight renders with old ID should abort
    const currentRenderId = ++renderIdRef.current;

    const renderPage = async () => {
      if (!canvasRef.current) {
        return;
      }

      // Cancel any ongoing render task
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      try {
        setLoading(true);
        const page = await pdfDoc.getPage(pageNum);

        // CHECK 1: Abort if a newer render started while we were waiting
        if (currentRenderId !== renderIdRef.current) {
          return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
          return;
        }

        const context = canvas.getContext('2d');
        if (!context) {
          return;
        }

        // Guard: Skip if containerWidth too small (avoids negative/zero scale)
        if (containerWidth <= 32) {
          return;
        }

        // Calculate scale to fit width (user scrolls vertically)
        const viewport = page.getViewport({ scale: 1 });
        const fitWidthScale = (containerWidth - 32) / viewport.width;
        const finalScale = fitWidthScale * scale;
        const scaledViewport = page.getViewport({ scale: finalScale });

        // Clear canvas before resize (prevents ghost content)
        context.clearRect(0, 0, canvas.width, canvas.height);

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        // CHECK 2: Abort if stale before rendering
        if (currentRenderId !== renderIdRef.current) {
          return;
        }

        renderTaskRef.current = page.render({
          canvasContext: context,
          viewport: scaledViewport
        });

        await renderTaskRef.current.promise;

        // CHECK 3: Don't update state if stale
        if (currentRenderId !== renderIdRef.current) {
          return;
        }

        renderTaskRef.current = null;
        setLoading(false);
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'name' in err && err.name !== 'RenderingCancelledException') {
          console.error('Failed to render page:', err);
        }
        // Only update loading state if this is still the current render
        if (currentRenderId === renderIdRef.current) {
          setLoading(false);
        }
      }
    };

    renderPage();

    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNum, scale, containerWidth]);

  return (
    <div className="relative flex justify-center">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="shadow-lg bg-white"
      />
    </div>
  );
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  className,
  showBottomControls = true,
  showThumbnails = true,
  initialPage = 1,
  singlePageMode = false
}) => {
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(singlePageMode ? initialPage : 1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [thumbnailsCollapsed, setThumbnailsCollapsed] = useState(false);
  const [thumbnailScale, setThumbnailScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(600);

  // Flag to prevent scroll sync loops
  const isScrollingFromThumbnail = useRef(false);

  // Computed thumbnail width
  const thumbnailWidth = DEFAULT_THUMBNAIL_WIDTH * thumbnailScale;
  const sidebarWidth = thumbnailWidth + 24; // padding

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        setPdfDoc(null);
        setCurrentPage(1);

        const loadingTask = pdfjsLib.getDocument({
          url,
          cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.394/cmaps/',
          cMapPacked: true
        });

        const pdf = await loadingTask.promise;

        if (!cancelled) {
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load PDF:', err);
          setError('Failed to load PDF document');
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Track container width for responsive scaling
  // Re-run when loading changes so we can observe the container after PDF loads
  useEffect(() => {
    if (!mainScrollRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const newWidth = entry.contentRect.width;
        // Only update if change is significant (>5px) - avoids scrollbar jitter race conditions
        setContainerWidth(prev => Math.abs(prev - newWidth) > 5 ? newWidth : prev);
      }
    });

    resizeObserver.observe(mainScrollRef.current);
    return () => resizeObserver.disconnect();
  }, [loading]);

  // IntersectionObserver to track visible page
  useEffect(() => {
    if (!pdfDoc || !mainScrollRef.current) {
return;
}

    const observer = new IntersectionObserver(
      (entries) => {
        // Don't update if we're programmatically scrolling from thumbnail click
        if (isScrollingFromThumbnail.current) {
return;
}

        // Find the most visible page
        let mostVisiblePage = currentPage;
        let maxRatio = 0;

        entries.forEach(entry => {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            const pageNum = parseInt(entry.target.getAttribute('data-page') || '1');
            mostVisiblePage = pageNum;
          }
        });

        if (maxRatio > 0.1) {
          setCurrentPage(mostVisiblePage);
        }
      },
      {
        root: mainScrollRef.current,
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
      }
    );

    // Observe all page containers
    pageRefs.current.forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [pdfDoc, totalPages, currentPage]);

  // Scroll thumbnail to match current page
  useEffect(() => {
    if (!thumbnailScrollRef.current || thumbnailsCollapsed) {
return;
}

    const thumbnailEl = thumbnailScrollRef.current.querySelector(
      `[data-page="${currentPage}"]`
    );
    if (thumbnailEl) {
      thumbnailEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentPage, thumbnailsCollapsed]);

  // Click thumbnail → scroll main PDF
  const goToPage = useCallback((pageNum: number) => {
    if (pageNum < 1 || pageNum > totalPages) {
return;
}

    const pageEl = pageRefs.current.get(pageNum);
    if (pageEl && mainScrollRef.current) {
      isScrollingFromThumbnail.current = true;
      setCurrentPage(pageNum);

      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Reset flag after scroll animation completes
      setTimeout(() => {
        isScrollingFromThumbnail.current = false;
      }, 500);
    }
  }, [totalPages]);

  // Scroll to initial page when PDF loads
  useEffect(() => {
    if (!loading && pdfDoc && initialPage > 1 && initialPage <= totalPages) {
      // Small delay to ensure pages are rendered
      const timer = setTimeout(() => {
        goToPage(initialPage);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, pdfDoc, initialPage, totalPages, goToPage]);

  // Zoom handlers
  const zoomIn = () => {
    setScale(prev => Math.min(prev + SCALE_STEP, MAX_SCALE));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - SCALE_STEP, MIN_SCALE));
  };

  const resetZoom = () => {
    setScale(DEFAULT_SCALE);
  };

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("flex items-center justify-center h-full text-muted-foreground", className)}>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Top Controls */}
      <div className="flex items-center justify-between p-2 bg-background flex-shrink-0 border-b">
        {/* Left: Thumbnail toggle + Page indicator */}
        <div className="flex items-center gap-2">
          {showThumbnails && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setThumbnailsCollapsed(!thumbnailsCollapsed)}
              className="h-7 w-7 p-0"
              title={thumbnailsCollapsed ? 'Show thumbnails' : 'Hide thumbnails'}
            >
              {thumbnailsCollapsed ? (
                <PanelLeft className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* Page indicator with navigation for single page mode */}
          {singlePageMode ? (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="h-7 w-7 p-0"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground min-w-[80px] text-center">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="h-7 w-7 p-0"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
          )}
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE}
            className="h-7 w-7 p-0"
            title="Zoom Out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE}
            className="h-7 w-7 p-0"
            title="Zoom In"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetZoom}
            className="h-7 w-7 p-0"
            title="Reset Zoom"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Main content area with thumbnails and PDF */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Thumbnail Sidebar */}
        {showThumbnails && !thumbnailsCollapsed && pdfDoc && (
          <div
            className="flex-shrink-0 border-r bg-muted/30 flex flex-col"
            style={{ width: sidebarWidth }}
          >
            {/* Thumbnail size slider */}
            <div className="p-1.5 border-b flex items-center gap-1.5 bg-background/50">
              <Minus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <Slider
                value={[thumbnailScale]}
                min={MIN_THUMBNAIL_SCALE}
                max={MAX_THUMBNAIL_SCALE}
                step={0.1}
                onValueChange={([val]) => setThumbnailScale(val)}
                className="flex-1"
              />
              <Plus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            </div>

            {/* Thumbnails - independent scroll */}
            <ScrollArea className="flex-1">
              <div ref={thumbnailScrollRef} className="flex flex-col gap-1 p-1.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                  <div key={pageNum} data-page={pageNum}>
                    <PageThumbnail
                      pdfDoc={pdfDoc}
                      pageNum={pageNum}
                      isSelected={pageNum === currentPage}
                      onClick={() => goToPage(pageNum)}
                      thumbnailWidth={thumbnailWidth}
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* PDF Pages - Single page or Continuous Scroll */}
        <div
          ref={mainScrollRef}
          className="flex-1 overflow-auto bg-muted/50 min-h-0"
        >
          <div className={cn(
            "flex flex-col items-center p-4",
            singlePageMode ? "h-full justify-center" : "gap-4"
          )}>
            {pdfDoc && (singlePageMode
              ? (
                  // Single page mode - only render current page
                  <div className="w-full flex justify-center">
                    <PageCanvas
                      pdfDoc={pdfDoc}
                      pageNum={currentPage}
                      scale={scale}
                      containerWidth={containerWidth}
                    />
                  </div>
                )
              : (
                  // Continuous scroll mode - render all pages
                  Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                    <div
                      key={pageNum}
                      data-page={pageNum}
                      ref={(el) => {
                        if (el) {
                          pageRefs.current.set(pageNum, el);
                        }
                      }}
                      className="w-full flex justify-center"
                    >
                      <PageCanvas
                        pdfDoc={pdfDoc}
                        pageNum={pageNum}
                        scale={scale}
                        containerWidth={containerWidth}
                      />
                    </div>
                  ))
                )
            )}
          </div>
        </div>
      </div>

      {/* Bottom Controls (optional) */}
      {showBottomControls && (
        <div className="flex items-center justify-between p-2 bg-background flex-shrink-0 border-t">
          {/* Page indicator with navigation for single page mode */}
          {singlePageMode ? (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="h-7 w-7 p-0"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground min-w-[80px] text-center">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="h-7 w-7 p-0"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
          )}

          {/* Zoom Controls */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              className="h-7 w-7 p-0"
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground min-w-[50px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              className="h-7 w-7 p-0"
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetZoom}
              className="h-7 w-7 p-0"
              title="Reset Zoom"
            >
              <RotateCw className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
